// Utils tính toán cho exam matrix

import { DangToan, ExamMatrixRow, QuestionCell, DIEM_MOI_CAU } from '../types/exam-matrix'

/**
 * Tính tổng điểm cho một dạng toán
 */
export const calculateDangScore = (dang: DangToan): number => {
  let score = 0
  
  // TNKQ - Nhiều lựa chọn (0.25 điểm/câu)
  score += (dang.tnkq.nhieuLuaChon.biet.soCauYeuCau +
            dang.tnkq.nhieuLuaChon.hieu.soCauYeuCau +
            dang.tnkq.nhieuLuaChon.vanDung.soCauYeuCau) * DIEM_MOI_CAU.TN
  
  // TNKQ - Đúng Sai (1 điểm/câu)
  score += (dang.tnkq.dungSai.biet.soCauYeuCau +
            dang.tnkq.dungSai.hieu.soCauYeuCau +
            dang.tnkq.dungSai.vanDung.soCauYeuCau) * DIEM_MOI_CAU.DS
  
  // TNKQ - Trả lời ngắn (0.5 điểm/câu)
  score += (dang.tnkq.traLoiNgan.biet.soCauYeuCau +
            dang.tnkq.traLoiNgan.hieu.soCauYeuCau +
            dang.tnkq.traLoiNgan.vanDung.soCauYeuCau) * DIEM_MOI_CAU.TL
  
  // Tự luận (0.5 điểm/câu)
  score += (dang.tuLuan.biet.soCauYeuCau +
            dang.tuLuan.hieu.soCauYeuCau +
            dang.tuLuan.vanDung.soCauYeuCau) * DIEM_MOI_CAU.TL
  
  return score
}

/**
 * Tính tổng điểm cho một row
 */
export const calculateRowScore = (row: ExamMatrixRow): number => {
  return row.cacDang.reduce((total, dang) => total + calculateDangScore(dang), 0)
}

/**
 * Tính tổng điểm cho toàn bộ ma trận
 */
export const calculateMatrixTotalScore = (rows: ExamMatrixRow[]): number => {
  return rows.reduce((total, row) => total + calculateRowScore(row), 0)
}

/**
 * Tính % điểm cho một row
 */
export const calculateRowPercentage = (row: ExamMatrixRow, totalScore: number): number => {
  if (totalScore === 0) return 0
  return (calculateRowScore(row) / totalScore) * 100
}

/**
 * Tính tổng số câu hỏi
 */
export const countTotalQuestions = (rows: ExamMatrixRow[]): number => {
  return rows.reduce((total, row) => {
    return total + row.cacDang.reduce((rowTotal, dang) => {
      return rowTotal + countDangQuestions(dang)
    }, 0)
  }, 0)
}

/**
 * Đếm số câu trong một dạng
 */
export const countDangQuestions = (dang: DangToan): number => {
  let count = 0
  
  // TNKQ
  count += dang.tnkq.nhieuLuaChon.biet.soCauYeuCau
  count += dang.tnkq.nhieuLuaChon.hieu.soCauYeuCau
  count += dang.tnkq.nhieuLuaChon.vanDung.soCauYeuCau
  
  count += dang.tnkq.dungSai.biet.soCauYeuCau
  count += dang.tnkq.dungSai.hieu.soCauYeuCau
  count += dang.tnkq.dungSai.vanDung.soCauYeuCau
  
  count += dang.tnkq.traLoiNgan.biet.soCauYeuCau
  count += dang.tnkq.traLoiNgan.hieu.soCauYeuCau
  count += dang.tnkq.traLoiNgan.vanDung.soCauYeuCau
  
  // Tự luận
  count += dang.tuLuan.biet.soCauYeuCau
  count += dang.tuLuan.hieu.soCauYeuCau
  count += dang.tuLuan.vanDung.soCauYeuCau
  
  return count
}

/**
 * Tính tổng theo từng cột (VD: tất cả câu "Biết" của "Nhiều lựa chọn")
 */
export const calculateColumnSum = (rows: ExamMatrixRow[], columnPath: string): number => {
  return rows.reduce((sum, row) => {
    return sum + row.cacDang.reduce((rowSum, dang) => {
      const value = getNestedValue(dang, columnPath)
      return rowSum + (value || 0)
    }, 0)
  }, 0)
}

/**
 * Helper: Lấy giá trị nested theo path
 */
const getNestedValue = (obj: any, path: string): number => {
  const parts = path.split('.')
  let current = obj
  
  for (const part of parts) {
    if (current === null || current === undefined) return 0
    current = current[part]
  }
  
  // Nếu là QuestionCell, lấy soCauYeuCau
  if (current && typeof current === 'object' && 'soCauYeuCau' in current) {
    return (current as QuestionCell).soCauYeuCau
  }
  
  return typeof current === 'number' ? current : 0
}

/**
 * Kiểm tra ma trận có hợp lệ không
 */
export const isMatrixValid = (rows: ExamMatrixRow[]): boolean => {
  for (const row of rows) {
    for (const dang of row.cacDang) {
      // Kiểm tra tất cả các cell
      const allCells = [
        ...Object.values(dang.tnkq.nhieuLuaChon),
        ...Object.values(dang.tnkq.dungSai),
        ...Object.values(dang.tnkq.traLoiNgan),
        ...Object.values(dang.tuLuan)
      ]
      
      for (const cell of allCells) {
        if (!cell.isValid) {
          return false
        }
      }
    }
  }
  
  return true
}

